import { z } from 'zod'

import {
  ALWAYS_SENT_FLAGS,
  PLACE_FEATURE_GROUP_TAB,
  PLACE_FEATURES,
  PlaceFeatureDefinition,
  PlaceFeatureGroup,
  PlaceFeatureKey,
  PlaceFeatureTab,
  PlaceFlags,
} from './places-features.constants'

export const PlaceFeatureWarningIdSchema = z.enum([
  'accountSuspendedOn',
  'cascadeOff',
  'iglooMasterKeyOff',
  'iglooStaffAccess',
  'iglooVisitorAccessOff',
  'iglooVisitorAccessOn',
])

export const PlaceFeatureWarningId = PlaceFeatureWarningIdSchema.enum

export type PlaceFeatureWarningId = z.infer<typeof PlaceFeatureWarningIdSchema>

export interface PlaceFeatureWarning {
  id: PlaceFeatureWarningId
  severity: 'destructive' | 'info'
  features: PlaceFeatureKey[]
}

export type FeatureDisabledReason =
  | { type: 'admin' }
  | { type: 'parent'; key: string }
  | { type: 'requires'; key: string }

export interface FeatureState {
  disabled: boolean
  reason?: FeatureDisabledReason
}

type FlagSource = Record<string, unknown> | null | undefined

const FEATURES: readonly PlaceFeatureDefinition[] = PLACE_FEATURES

const DEFINITIONS: ReadonlyMap<string, PlaceFeatureDefinition> = new Map(
  FEATURES.map((definition) => [definition.key, definition]),
)

export const getFeatureDefinition = (key: string) => DEFINITIONS.get(key)

export const getFeaturesByGroup = (group: PlaceFeatureGroup) =>
  FEATURES.filter((definition) => definition.group === group)

export const getFeaturesByTab = (tab: PlaceFeatureTab) =>
  FEATURES.filter((definition) => PLACE_FEATURE_GROUP_TAB[definition.group] === tab)

export const getFeatureChildren = (key: string) =>
  FEATURES.filter((definition) => definition.parent === key)

const getDependents = (key: string) =>
  FEATURES.filter((definition) => {
    if (definition.parent === key) return true
    return !!definition.requires?.includes(key)
  })

export const getDependentKeys = (key: string): string[] => {
  const result: string[] = []
  const visit = (current: string) => {
    getDependents(current).forEach((definition) => {
      if (result.includes(definition.key)) return
      result.push(definition.key)
      visit(definition.key)
    })
  }
  visit(key)
  return result
}

const isOn = (flags: PlaceFlags, key: string) => !!flags[key as PlaceFeatureKey]

const getUnmetDependency = (definition: PlaceFeatureDefinition, flags: PlaceFlags) => {
  if (definition.parent && !isOn(flags, definition.parent)) {
    return { key: definition.parent, type: 'parent' } as const
  }
  const unmet = definition.requires?.find((key) => !isOn(flags, key))
  return unmet ? ({ key: unmet, type: 'requires' } as const) : null
}

export const toFlags = (source: FlagSource): PlaceFlags =>
  Object.fromEntries(FEATURES.map(({ key }) => [key, !!source?.[key]])) as PlaceFlags

export const applyFeatureChange = (
  flags: PlaceFlags,
  key: string,
  checked: boolean,
): PlaceFlags => {
  const next: PlaceFlags = { ...flags, [key]: checked }
  if (!checked) {
    getDependentKeys(key).forEach((dependent) => {
      if (dependent in next) next[dependent as PlaceFeatureKey] = false
    })
  }
  return next
}

export const getFeatureState = (
  definition: PlaceFeatureDefinition,
  flags: PlaceFlags,
  { isAdmin }: { isAdmin: boolean },
): FeatureState => {
  if (definition.adminOnly && !isAdmin) return { disabled: true, reason: { type: 'admin' } }

  const unmet = getUnmetDependency(definition, flags)
  return unmet ? { disabled: true, reason: unmet } : { disabled: false }
}

export const resolveFlags = (flags: PlaceFlags): PlaceFlags => {
  const next = { ...flags }
  let changed = true

  while (changed) {
    changed = false
    FEATURES.forEach((definition) => {
      if (isOn(next, definition.key) && getUnmetDependency(definition, next)) {
        next[definition.key as PlaceFeatureKey] = false
        changed = true
      }
    })
  }

  return next
}

export const getOrphanKeys = (flags: PlaceFlags): string[] => {
  const resolved = resolveFlags(flags)
  return FEATURES.filter(({ key }) => isOn(flags, key) && !isOn(resolved, key)).map(
    ({ key }) => key,
  )
}

// Note: stored children whose parent is already off (the backend never cleaned them up) are left as
// they are; only the children of a switch turned off in this save are cascaded
const resolveChange = (base: PlaceFlags, next: PlaceFlags): PlaceFlags => {
  const merged = { ...base, ...next }
  const effective = resolveFlags(merged)

  FEATURES.forEach((definition) => {
    const key = definition.key as PlaceFeatureKey
    const isStoredOrphan = isOn(base, key) && !!getUnmetDependency(definition, base)
    if (isStoredOrphan && isOn(merged, key) === isOn(base, key)) effective[key] = base[key]
  })

  return effective
}

const getDerivedIglooStaffAccess = (flags: PlaceFlags) =>
  isOn(flags, 'iglooWorkAvailable') ? isOn(flags, 'keylessAccessForStaffAvailable') : false

export const getFeatureWarnings = (server: FlagSource, next: PlaceFlags): PlaceFeatureWarning[] => {
  const base = toFlags(server)
  const effective = resolveChange(base, next)
  const turnedOff = (key: PlaceFeatureKey) => isOn(base, key) && !isOn(effective, key)
  const turnedOn = (key: PlaceFeatureKey) => !isOn(base, key) && isOn(effective, key)
  const warnings: PlaceFeatureWarning[] = []

  if (turnedOff('iglooWorkVisitorAccessAvailable')) {
    warnings.push({
      features: ['iglooWorkVisitorAccessAvailable'],
      id: PlaceFeatureWarningId.iglooVisitorAccessOff,
      severity: 'destructive',
    })
  }
  if (turnedOn('iglooWorkVisitorAccessAvailable')) {
    warnings.push({
      features: ['iglooWorkVisitorAccessAvailable'],
      id: PlaceFeatureWarningId.iglooVisitorAccessOn,
      severity: 'info',
    })
  }
  if (turnedOff('iglooWorkStaffMasterKeyAvailable')) {
    warnings.push({
      features: ['iglooWorkStaffMasterKeyAvailable'],
      id: PlaceFeatureWarningId.iglooMasterKeyOff,
      severity: 'destructive',
    })
  }
  if (getDerivedIglooStaffAccess(effective) !== isOn(base, 'iglooWorkStaffAccessAvailable')) {
    warnings.push({
      features: ['keylessAccessForStaffAvailable'],
      id: PlaceFeatureWarningId.iglooStaffAccess,
      severity: 'info',
    })
  }
  if (turnedOn('accountSuspended')) {
    warnings.push({
      features: ['accountSuspended'],
      id: PlaceFeatureWarningId.accountSuspendedOn,
      severity: 'destructive',
    })
  }

  const cascaded = FEATURES.filter((definition) => {
    if (definition.hidden || !turnedOff(definition.key as PlaceFeatureKey)) return false
    return !!getUnmetDependency(definition, effective)
  }).map(({ key }) => key as PlaceFeatureKey)

  if (cascaded.length) {
    warnings.push({
      features: cascaded,
      id: PlaceFeatureWarningId.cascadeOff,
      severity: 'info',
    })
  }

  return warnings
}

export const buildFlagsPayload = ({
  isAdmin,
  next,
  server,
}: {
  isAdmin: boolean
  next: PlaceFlags
  server: FlagSource
}): Partial<Record<PlaceFeatureKey, boolean>> => {
  const base = toFlags(server)
  const effective = resolveChange(base, next)
  const payload: Partial<Record<PlaceFeatureKey, boolean>> = {}

  FEATURES.forEach((definition) => {
    const key = definition.key as PlaceFeatureKey
    if (definition.hidden || (definition.adminOnly && !isAdmin)) return
    if (isOn(effective, key) === isOn(base, key)) return

    payload[key] = isOn(effective, key)
    if (isOn(effective, key) && definition.parent) {
      payload[definition.parent as PlaceFeatureKey] = isOn(effective, definition.parent)
    }
  })

  ALWAYS_SENT_FLAGS.forEach((key) => {
    payload[key] = isOn(effective, key)
  })

  const derivedStaffAccess = getDerivedIglooStaffAccess(effective)
  if (derivedStaffAccess !== isOn(base, 'iglooWorkStaffAccessAvailable')) {
    payload.iglooWorkStaffAccessAvailable = derivedStaffAccess
  }

  return payload
}
